import { Box, Button, Card, Flex, Group, Text, ThemeIcon } from '@mantine/core';
import { IconClipboardText } from '@tabler/icons-react';
import { useCallback, useEffect, useRef, useState, type MutableRefObject } from 'react';
import { DateTimeTooltip } from '@/components/Timeline/TimelineItems/DateTimeTooltip';
import { Encounter, QuestionnaireResponse, Task } from '@medplum/fhirtypes';
import { ENCOUNTER_AUTOSAVE_DEBOUNCE_MS } from './debounceTiming';
import { TaskQuestionnaireForm, type TaskQuestionnaireFormHandle } from './TaskQuestionnaireForm';
import { SignAndLockEncounterButton } from '@/components/SignAndLockEncounterButton';
import { EncounterStatusBadge } from '@/components/EncounterStatusBadge';
import { EncounterStatus } from '@bariendo/lib/constants';
import { useSignEncounter } from '@/pages/PatientTimelinePage/hooks/useSignEncounter';
import { useSaveEncounterDraft } from '@/pages/PatientTimelinePage/hooks/useSaveEncounterDraft';
import { usePreventNumberInputScroll } from '@/hooks/usePreventNumberInputScroll';

/** Full merge: every open task (for explicit save draft / sign). */
function buildResponsesForPersist(
  tasks: Task[],
  pending: Record<string, QuestionnaireResponse>,
  formRefs: MutableRefObject<Record<string, TaskQuestionnaireFormHandle | null>>
): Record<string, QuestionnaireResponse> {
  const merged: Record<string, QuestionnaireResponse> = { ...pending };

  for (const task of tasks) {
    if (task.status === 'completed' || !task.id) {
      continue;
    }
    const live = formRefs.current[task.id]?.getLatestResponse();
    if (live) {
      merged[task.id] = live;
    }
  }
  return merged;
}

/** Only tasks with pending edits; uses live form state per dirty task (e.g. BMI debounce). */
function buildDirtyResponsesForPersist(
  pending: Record<string, QuestionnaireResponse>,
  formRefs: MutableRefObject<Record<string, TaskQuestionnaireFormHandle | null>>
): Record<string, QuestionnaireResponse> {
  const merged: Record<string, QuestionnaireResponse> = {};
  for (const taskId of Object.keys(pending)) {
    const live = formRefs.current[taskId]?.getLatestResponse();
    merged[taskId] = live ?? pending[taskId]!;
  }
  return merged;
}

export function EncounterTimelineComponent({
  encounter,
  tasks,
  patientName,
  onSaveDraft,
  onSignEncounter,
  handleNavigateToItem,
}: {
  patientName: string;
  encounter: Encounter;
  tasks: Task[];
  onSaveDraft: () => void;
  onSignEncounter: () => void;
  handleNavigateToItem?: (itemReference: string) => void;
}) {
  const [pendingResponses, setPendingResponses] = useState<Record<string, QuestionnaireResponse>>({});
  const pendingResponsesRef = useRef<Record<string, QuestionnaireResponse>>({});
  const formRefs = useRef<Record<string, TaskQuestionnaireFormHandle | null>>({});

  const registerFormRef = useCallback((taskId: string) => {
    return (instance: TaskQuestionnaireFormHandle | null): void => {
      formRefs.current[taskId] = instance;
    };
  }, []);

  const { handleSaveDraft, isSavingDraft } = useSaveEncounterDraft(
    pendingResponses,
    setPendingResponses,
    onSaveDraft,
    encounter,
    handleNavigateToItem,
    tasks,
    pendingResponsesRef
  );
  const { isSigning, isSigningSuccess, handleSignEncounter } = useSignEncounter(handleNavigateToItem, onSignEncounter);

  const handleSaveDraftRef = useRef(handleSaveDraft);
  handleSaveDraftRef.current = handleSaveDraft;
  const formContainerRef = useRef<HTMLDivElement>(null);
  const autosaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // The pending edits whose autosave failed: retried on the next real edit, not every time
  // isSavingDraft flips back (that loop wrote a new response every ~2s, BAR-3112).
  const failedAutosaveRef = useRef<Record<string, QuestionnaireResponse> | null>(null);
  pendingResponsesRef.current = pendingResponses;

  usePreventNumberInputScroll(formContainerRef);

  const handleChangeResponse = (task: Task, response: QuestionnaireResponse): void => {
    if (!task.id) {
      return;
    }

    setPendingResponses((prev) => {
      const next = { ...prev, [task.id as string]: response };
      pendingResponsesRef.current = next;
      return next;
    });
  };

  useEffect(() => {
    if (autosaveTimerRef.current) {
      clearTimeout(autosaveTimerRef.current);
      autosaveTimerRef.current = null;
    }

    // Only autosave after real edits; persist dirty tasks only (one PUT per edited questionnaire).
    if (!Object.keys(pendingResponses).length || isSavingDraft || pendingResponses === failedAutosaveRef.current) {
      return;
    }

    const autosave = async (): Promise<void> => {
      const pending = pendingResponsesRef.current;
      if (!Object.keys(pending).length) {
        return;
      }
      const merged = buildDirtyResponsesForPersist(pending, formRefs);
      if (!Object.keys(merged).length) {
        return;
      }
      const saved = await handleSaveDraftRef.current({
        closeAfterSave: false,
        silent: true,
        responsesOverride: merged,
      });
      failedAutosaveRef.current = saved ? null : pending;
    };

    autosaveTimerRef.current = setTimeout(() => {
      autosaveTimerRef.current = null;
      void autosave();
    }, ENCOUNTER_AUTOSAVE_DEBOUNCE_MS);

    return () => {
      if (autosaveTimerRef.current) {
        clearTimeout(autosaveTimerRef.current);
        autosaveTimerRef.current = null;
      }
    };
  }, [pendingResponses, isSavingDraft, tasks]);

  const handleSaveDraftButtonClick = (): void => {
    const merged = buildResponsesForPersist(tasks, pendingResponsesRef.current, formRefs);
    if (!Object.keys(merged).length) {
      void handleSaveDraft({ closeOnly: true });
      return;
    }
    void handleSaveDraft({ responsesOverride: merged });
  };

  const formatDate = (dateString?: string): string => {
    if (!dateString) return 'Unknown date';
    return new Date(dateString).toLocaleDateString();
  };

  return (
    <Card withBorder padding={0} radius="lg" bg="white">
      <Box>
        <Group w="100%" p="xs" justify="center" align="center" style={{ borderBottom: '1px solid #DEE2E6' }}>
          <ThemeIcon variant="filled" radius="lg" size="lg" bg="blue-6">
            <IconClipboardText size={16} />
          </ThemeIcon>
          <Box flex={1}>
            <Group gap={0}>
              <Text variant="header" mr="10px">
                {tasks[0]?.basedOn?.[0]?.display}
              </Text>
              <EncounterStatusBadge status={EncounterStatus.EDITING} />
            </Group>
            <Group gap={0}>
              <Text variant="caption">{formatDate(encounter.period?.start)}</Text>
              <Text variant="caption" mx="xs">
                •
              </Text>
              <Text variant="caption">{patientName}</Text>
            </Group>
          </Box>
          <Group gap={0}>
            <DateTimeTooltip date={encounter.period?.start} />
          </Group>
        </Group>
        <Box p="md">
          <Box ref={formContainerRef}>
            {tasks.map((task) => (
              <TaskQuestionnaireForm
                key={task.id}
                ref={registerFormRef(task.id as string)}
                task={task as Task}
                onChangeResponse={handleChangeResponse}
              />
            ))}
          </Box>
          <Flex justify="flex-end" mt="sm">
            <Button
              variant="white"
              c="#228be6"
              onClick={handleSaveDraftButtonClick}
              loading={isSavingDraft}
              disabled={isSavingDraft || isSigning}
            >
              Save draft
            </Button>
            <SignAndLockEncounterButton
              encounter={encounter}
              loading={!!isSigning}
              closeSignModal={isSigningSuccess}
              onSign={() =>
                handleSignEncounter(encounter, buildResponsesForPersist(tasks, pendingResponses, formRefs), tasks)
              }
            />
          </Flex>
        </Box>
      </Box>
    </Card>
  );
}
